import * as React from "react";
import { LinksProps } from "./typings";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkLink } from "uitk-react-link";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DefaultFlexModuleResult, Link } from "typings/microserviceModels/links-flex-module";
import { LocalizedText } from "bernie-l10n";
import { GeoType } from "src/typings/flexFramework/FlexDefinitions";
import { LocationType } from "src/components/utility/LocationUtils";
import { InsideTabGroupContext } from "components/shared/InsideTabGroupContext/InsideTabGroupContext";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { observer } from "mobx-react";
import { withStores } from "src/stores";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";
import {
  ToggleAllContainerContext,
  ToggleContainerState,
} from "src/components/flexComponents/ToggleContainer/views/ToggleAllContainer";
import { getFmId } from "src/stores/ExperienceTemplateStore";

const getUitkLink = (model: DefaultFlexModuleResult, el: Link, rfrrid: string) => {
  const text = `${el.text} ${(el.auxiliaryText && `(${el.auxiliaryText})`) || ""}`;

  if (!el.url) {
    return (
      <UitkText inline size={300}>
        {text}
      </UitkText>
    );
  }

  return (
    <UitkLink>
      <TrackedLink moduleName={model.name} href={el.url} rfrr={rfrrid} linkName={el.text}>
        {text}
      </TrackedLink>
    </UitkLink>
  );
};

const SeeAllContentLink = ({
  model,
  location,
  locationType,
}: {
  model: DefaultFlexModuleResult;
  location: string;
  locationType?: GeoType;
}) => {
  const useNearPreposition =
    locationType &&
    (locationType === LocationType.AIRPORT ||
      locationType === LocationType.POINT_OF_INTEREST ||
      locationType === LocationType.METRO_STATION ||
      locationType === LocationType.TRAIN_STATION);

  const linksMessage = useNearPreposition ? "links.seeAllHotelsNear" : "links.seeAllHotels";

  return (
    <UitkLink>
      <TrackedLink
        moduleName={model.name}
        href={model.seeAllHotelLinks}
        rfrr={model.strategy}
        linkName={model.seeAllHotelLinks}
      >
        <LocalizedText message={linksMessage} data={[location]} />
      </TrackedLink>
    </UitkLink>
  );
};

/**
 * Card with links. Handled three different ways. Either it will display
 * auxiliary content in parenthesis, with an additional card to see more info,
 * or none of the former.
 */
export const Links = withStores(
  "flexModuleModelStore",
  "context",
  "flexViewModel"
)(
  observer((props: LinksProps) => {
    const { templateComponent, flexModuleModelStore, insideTabGroupParam, context, flexViewModel } = props;
    const pageName = flexViewModel.trackingContext.pageName;
    const moduleName = "editorial";
    const linkName = "linkstextlink";
    const sectionName = "content";

    if (!templateComponent) {
      return null;
    }
    const {
      metadata: { id },
      config: { fmTitleId },
    } = templateComponent;
    const fmId = getFmId(templateComponent);
    const model = flexModuleModelStore.getModel(id) as DefaultFlexModuleResult | null;
    const [toggle, setToggle] = React.useState(model?.toggle === "collapsed");
    let insideTabGroup = React.useContext(InsideTabGroupContext);
    insideTabGroup = insideTabGroupParam || insideTabGroup;
    if (!model || !model.links || model.links.length === 0) {
      return null;
    }

    const DEFAULT_MOBILE_DISPLAY_COLUMNS = 1;

    const displayColumns = Number(model.displayColumns) as UitkLayoutGridColumnRow;

    const location = model.locationName || context.searchContext.location?.name;
    const locationType = model.locationType || context.searchContext.location?.type;
    const keyHelper = new ItemKeyHelper("links");
    const containerPadding: UitkSpacingProps["padding"] = insideTabGroup ? {} : { blockstart: "three" };
    const tabPanelPadding: UitkSpacingProps["padding"] =
      toggle && !insideTabGroup ? { inline: "three", block: "three" } : { inline: "four", block: "two" };
    const toggleContainerContext = React.useContext(ToggleAllContainerContext);
    const isToggleDisabled = model.toggle === "none";
    const flipToggle = (): void => {
      if (isToggleDisabled) return;
      setToggle(!toggle);
      toggleContainerContext.toggleCollapseAll(ToggleContainerState.NEITHER);
    };
    const expandoKeyboardPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        flipToggle();
      }
    };

    React.useEffect(() => {
      if (toggleContainerContext.collapseAll === ToggleContainerState.ALL_EXPANDED) {
        setToggle(false);
      } else if (toggleContainerContext.collapseAll === ToggleContainerState.ALL_COLLAPSED) {
        setToggle(true);
      }
    }, [toggleContainerContext.collapseAll]);
    const borderTop = !insideTabGroup ? "top" : undefined;

    const links = (
      <>
        <UitkCardContentSection padded={false} border={borderTop}>
          {insideTabGroup && (
            <UitkSpacing margin={{ inline: "three" }}>
              <UitkHeading tag="h3" size={5}>
                {model.title}
              </UitkHeading>
            </UitkSpacing>
          )}
          <UitkSpacing padding={tabPanelPadding}>
            <UitkLayoutGrid
              tag="ul"
              space="one"
              className="no-bullet"
              columns={{ small: DEFAULT_MOBILE_DISPLAY_COLUMNS, large: displayColumns }}
            >
              {model.links.map((el, index) => (
                <UitkLayoutGridItem colSpan={1} key={keyHelper.next()}>
                  <li>{getUitkLink(model, el, `${pageName}.${moduleName}.${linkName}${index + 1}.${sectionName}`)}</li>
                </UitkLayoutGridItem>
              ))}
            </UitkLayoutGrid>
          </UitkSpacing>
        </UitkCardContentSection>
        {model.seeAllHotelLinks && location && (
          <UitkSpacing padding={{ block: "two" }}>
            <UitkCardContentSection border="top">
              <SeeAllContentLink model={model} location={location} locationType={locationType} />
            </UitkCardContentSection>
          </UitkSpacing>
        )}
      </>
    );

    return (
      <LazyLoad context={context}>
        <UitkSpacing padding={containerPadding}>
          <div className="Links" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
            {!insideTabGroup ? (
              <UitkCard>
                <div
                  className="CallToAction"
                  tabIndex={0}
                  role="button"
                  aria-expanded={!toggle}
                  onClick={flipToggle}
                  data-testid="expando-toggle"
                  onKeyDown={expandoKeyboardPress}
                >
                  <UitkCardContentSection padded>
                    <UitkLayoutFlex>
                      <UitkLayoutFlexItem grow={1}>
                        <UitkText size={500} weight="bold" theme="default">
                          {model.title}
                        </UitkText>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem alignSelf="center" grow={0}>
                        {!isToggleDisabled && (
                          <UitkIcon name={!toggle ? "expand_less" : "expand_more"} size={UitkIconSize.SMALL} />
                        )}
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </UitkCardContentSection>
                </div>
                <UitkCardContentSection padded={false} className={`${!toggle ? "" : "is-hidden"}`}>
                  <UitkSpacing padding={{ blockend: "one", inline: "one" }}>
                    <div>{links}</div>
                  </UitkSpacing>
                </UitkCardContentSection>
              </UitkCard>
            ) : (
              <UitkCard>{links}</UitkCard>
            )}
          </div>
        </UitkSpacing>
      </LazyLoad>
    );
  })
);

export default Links;
